import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { FieldError } from 'react-hook-form';
import t from 'react-translate';
import { NvTextInput } from 'shared/components/inputs/nv-text-input';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { config } from '@config/pendo.config.json';

const MAX_NAME_LENGTH = 120;

type CreateDownloadModalProps = {
  show: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
};

const CreateDownloadModal = ({ show, onClose, onCreate }: CreateDownloadModalProps) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNameTooLong = name.length > MAX_NAME_LENGTH;

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting || isNameTooLong) return;
    setIsSubmitting(true);
    try {
      await onCreate(name);
      setName('');
    } catch {
      // error already handled by caller
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NvModal
      type={ModalType.FIXED}
      show={show}
      height={400}
      width={560}
      header={t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.TITLE()}
      onClose={handleClose}
      body={(
        <div className='d-flex flex-column align-items-center'>
          <div className='section-title mb-2'>
            {t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.NAME_YOUR_DOWNLOAD()}
          </div>
          <NvTextInput
            value={name}
            placeholder={t.LEARNING_JOURNEYS.DATA_DOWNLOADS.CREATE_NEW_DOWNLOAD_MODAL.DEFAULT()}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleSubmit(); } }}
            disabled={isSubmitting}
            error={isNameTooLong
              ? ({ message: t.VALIDATION.MAX_LENGTH(String(MAX_NAME_LENGTH)) } as FieldError)
              : undefined}
            errorOnTouching={false}
            showErrorWithoutFocus
            data-qa={config.pendo.customDownloads.createModal.nameInput}
          />
          <div className='d-flex mt-4'>
            <Button
              variant='secondary'
              className='mr-2'
              onClick={handleClose}
              disabled={isSubmitting}
              data-qa={config.pendo.customDownloads.createModal.cancel}
            >
              {t.FORM.CANCEL()}
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              disabled={isSubmitting || isNameTooLong}
              data-qa={config.pendo.customDownloads.createModal.create}
            >
              {isSubmitting ? t.FORM.CREATING() : t.FORM.CREATE()}
            </Button>
          </div>
        </div>
      )}
    />
  );
};

export default CreateDownloadModal;
